import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MatSlideToggleChange,
  MatSlideToggleModule
} from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AlertService, VexPopoverRef } from '@pharvio/core/ui';
import { AuthState } from '@pharvio/accounts/admin-account/data-access';
import { IStorefrontAvailability } from '@pharvio/core/shared';
import {
  StorefrontAvailabilityState,
  StorefrontSettingsState,
  ToggleOnlineOrdering
} from '@pharvio/storefront/data-access';
import { DateTime } from 'luxon';
import { map } from 'rxjs/operators';
import { PERMISSIONS } from '../../../../core/constants/permissions.constant';
import { StopOnlineOrderingDialogComponent } from '../stop-online-ordering-dialog/stop-online-ordering-dialog.component';

const DEFAULT_TZ = 'Africa/Lagos';

/** Content of the header status pill popover (~320px). */
@Component({
  selector: 'app-availability-popover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDialogModule
  ],
  templateUrl: './availability-popover.component.html'
})
export class AvailabilityPopoverComponent {
  private readonly store = inject(Store);
  private readonly alert = inject(AlertService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly popoverRef = inject(VexPopoverRef);

  readonly currentAvailability$ = this.store.select(
    StorefrontAvailabilityState.currentAvailability
  );
  readonly isOnline$ = this.store.select(StorefrontAvailabilityState.isOnline);
  readonly actionInProgress$ = this.store.select(
    StorefrontAvailabilityState.actionInProgress
  );
  readonly canManage$ = this.store
    .select(AuthState.permissions)
    .pipe(
      map(
        (perms) =>
          perms.includes('*') ||
          perms.includes(PERMISSIONS.STOREFRONT.CONFIGURE)
      )
    );

  private get tz(): string {
    return this.store.selectSnapshot(StorefrontSettingsState.settings)?.timezone ||
      DEFAULT_TZ;
  }

  toggleOnlineOrders(current: boolean, event: MatSlideToggleChange): void {
    event.source.checked = current;

    if (!current) {
      this.dispatchToggle(true);
      return;
    }

    // Close the popover before opening the modal so its backdrop doesn't clash.
    this.popoverRef.close();
    this.dialog
      .open<StopOnlineOrderingDialogComponent, unknown, boolean>(
        StopOnlineOrderingDialogComponent,
        { width: '440px' }
      )
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.dispatchToggle(false);
      });
  }

  private dispatchToggle(enabled: boolean): void {
    this.store
      .dispatch(new ToggleOnlineOrdering({ enabled }))
      .subscribe({
        next: () =>
          this.alert.success(
            enabled ? 'Online ordering resumed.' : 'Online ordering stopped.'
          ),
        error: () =>
          this.alert.error("Couldn't update online ordering. Please try again.")
      });
  }

  manageAvailability(): void {
    this.popoverRef.close();
    this.router.navigate(['/storefront/availability']);
  }

  statusSubtitle(a: IStorefrontAvailability): string {
    const tz = this.tz;
    if (a.isOpen) {
      return a.closesAt ? `Closes at ${this.formatTime(a.closesAt, tz)}` : 'Open';
    }
    switch (a.reason) {
      case 'manual_off':
        return 'Online ordering is paused.';
      case 'holiday_closed':
        return a.closureReason ? `Closed: ${a.closureReason}` : 'Closed today';
      case 'outside_hours':
        return a.nextOpenAt
          ? `Reopens ${this.formatTime(a.nextOpenAt, tz)}`
          : 'Closed — outside opening hours';
      default:
        return 'Closed';
    }
  }

  private formatTime(iso: string, tz: string): string {
    return DateTime.fromISO(iso).setZone(tz).toFormat('h:mm a');
  }
}
